import type { AnalysisDocument } from "./fiche.js";
import type { DepositManifest, FileOrigin, FileRole } from "./prepare.js";

/** What every backend (Supabase in production, local PostgreSQL in tests, memory in the demo) provides. */
export interface DepositSummary {
  id: string;
  number: number;
  upload_status: "uploading" | "complete";
  integrity_status: "pending" | "verified" | "failed";
  analysis_status: "not_analyzed" | "analyzed" | "failed";
  slide_count: number;
  created_at: string;
  created_by: string;
  version: number;
}

export interface MissingObject {
  storage_path: string;
  sha256: string;
  bytes: number;
  media_type: string;
  /** An object already exists at this path with another size: it can never be replaced. */
  conflict: boolean;
}

export interface StartResult {
  outcome: "created" | "existing" | "resume" | "duplicate";
  deposit: DepositSummary;
  missing: MissingObject[];
}

export interface CompleteResult {
  outcome: "complete" | "incomplete";
  deposit: DepositSummary;
  missing: MissingObject[];
}

export interface DuplicateCheck {
  content_fingerprint: string;
  exact: DepositSummary | null;
  overlaps: Array<{ id: string; number: number; shared_images: number }>;
}

export interface DepositRow extends DepositSummary {
  source_kind: "zip" | "images";
  updated_at: string;
  completed_at: string | null;
  integrity_checked_at: string | null;
  note: string | null;
  instagram_url: string | null;
  published_at: string | null;
}

export interface DepositFileRow {
  id: string;
  deposit_id: string;
  role: FileRole;
  origin: FileOrigin;
  source_path: string;
  position: number | null;
  sha256: string;
  bytes: number;
  media_type: string;
  storage_path: string;
  integrity: "pending" | "verified" | "mismatch" | "missing";
  verified_at: string | null;
}

export interface DepositEventRow {
  id: number;
  deposit_id: string;
  at: string;
  actor: string | null;
  kind: string;
  detail: Record<string, unknown>;
}

export interface Member {
  user_id: string;
  display_name: string;
}

export interface DepositDetail {
  deposit: DepositRow;
  files: DepositFileRow[];
  events: DepositEventRow[];
}

export type UploadOutcome = "created" | "exists";

/**
 * The stored analysis of a deposit as the interface shows it: the compact summary written
 * by apps/deposit-analysis (docs/DEPOSIT_ANALYSIS.md), read from deposit_latest_analysis.
 * Measurements and the comparison with a reference are kept apart.
 */
export interface AnalysisSlide {
  position: number | null;
  status: "analyzed" | "failed";
  error?: string | null;
  /** read / failed / not_run. "read" without any line does not prove the slide has no text. */
  text_status?: "read" | "not_run" | "failed";
  text_error?: string;
  lines_read?: number;
  /** A slide split into two pictures, when the measurement sees a clear line between them. */
  composition?: { status: "split_detected" | "not_detected"; axis: "horizontal" | "vertical" | null; position: number | null };
  editorial?: Array<{ text: string; band: "top" | "middle" | "bottom"; on_light_block: boolean; uncertain: boolean }>;
  /** The brand's own mark: logo, handle, follow card (from 0.3.0; earlier, the logo is in `editorial`). */
  brand?: string[];
  in_photo?: string[];
  doubtful?: number;
  graphic_colors?: string[];
  photo_colors?: string[];
  size?: [number, number];
}

export interface AnalysisSummary {
  analyzer_version: string;
  generated_at: string;
  /** Every slide measured and its text read (absent from summaries written before 0.2.0). */
  complete?: boolean;
  slides_analyzed: number;
  slides_failed: number;
  text_read?: number;
  text_failed?: number;
  text_not_run?: number;
  text_read_empty?: number;
  lines_by_kind: { editorial: number; brand?: number; in_photo: number; doubtful: number };
  photo_colors: Array<{ hex: string; weight: number; count: number }>;
  graphic_colors: Array<{ hex: string; weight: number; count: number }>;
  light_block_lines: number;
  slides: AnalysisSlide[];
  reference_comparison: {
    reference: string;
    scope: string;
    status: string;
    note: string;
    items: Array<{ id: string; statement: string; consistent: number; inconsistent: number; uncertain: number; not_measured: number; cover: string | null }>;
  };
  limits: string[];
}

export interface DepositAnalysis {
  analyzer_version: string;
  created_at: string;
  /** False when the slides or their order changed since this analysis. */
  current: boolean;
  summary: AnalysisSummary;
}

/** The latest analysis job of a deposit: running, done, or failed with its error and partial result. */
export interface AnalysisJob {
  analyzer_version: string;
  status: "running" | "done" | "failed";
  attempts: number;
  last_error: string | null;
  partial_summary: AnalysisSummary | null;
  lease_until: string | null;
  updated_at: string;
  /** False when the slides or their order changed since this job. */
  current: boolean;
}

/**
 * Latest statistics of a deposit's Instagram publication (view deposit_publication_stats,
 * supabase/migrations/20261012090000_publication_stats.sql). Totals from publication up to
 * captured_at; a metric the source did not give is null, never 0; age_hours only when the
 * publication time is confirmed.
 */
export interface PublicationStats {
  matched: boolean;
  publication_type: string | null;
  published_local: string | null;
  published_at: string | null;
  captured_at: string | null;
  source: string | null;
  age_hours: number | null;
  reach: number | null;
  views: number | null;
  likes: number | null;
  comments: number | null;
  shares: number | null;
  saves: number | null;
  profile_visits: number | null;
  followers_gained: number | null;
}

export interface DepositBackend {
  /** The signed-in member, or null (no session, or an account that is not a member). */
  currentMember(): Promise<Member | null>;
  findDuplicates(zipSha256: string | null, imageSha256: string[]): Promise<DuplicateCheck>;
  startDeposit(clientKey: string, manifest: DepositManifest): Promise<StartResult>;
  /** Insert-only: an object already stored at this path is left untouched ("exists"). */
  uploadObject(storagePath: string, data: Uint8Array, mediaType: string): Promise<UploadOutcome>;
  completeDeposit(depositId: string): Promise<CompleteResult>;
  listDeposits(): Promise<DepositRow[]>;
  listMembers(): Promise<Member[]>;
  getDeposit(depositId: string): Promise<DepositDetail>;
  firstSlides(depositIds: string[]): Promise<Map<string, DepositFileRow>>;
  setSlideOrder(depositId: string, expectedVersion: number, slideIds: string[], extraIds: string[]): Promise<DepositSummary>;
  linkInstagram(depositId: string, expectedVersion: number, url: string | null, publishedAt: string | null): Promise<DepositSummary>;
  /** A short-lived URL to read (or download under `downloadName`) a stored object. */
  objectUrl(storagePath: string, downloadName?: string): Promise<string>;
  /** The latest stored analysis of a deposit, or null. Optional: backends without analyses omit it. */
  getAnalysis?(depositId: string): Promise<DepositAnalysis | null>;
  /** The latest analysis job of a deposit, or null. Optional, as getAnalysis. */
  getAnalysisJob?(depositId: string): Promise<AnalysisJob | null>;
  /** The full stored document of the latest analysis, read for the descriptive sheet (fiche.ts), or null. Optional, as getAnalysis. */
  getAnalysisDocument?(depositId: string): Promise<AnalysisDocument | null>;
  /** The latest statistics of the deposit's linked publication, or null (no link). Optional, as getAnalysis. */
  getPublicationStats?(depositId: string): Promise<PublicationStats | null>;
}

/** Errors a backend raises with a code the interface understands. */
export class BackendError extends Error {
  constructor(
    readonly code: "not_a_member" | "version_conflict" | "invalid" | "already_linked" | "not_found" | "network" | "unknown",
    message: string,
  ) {
    super(message);
    this.name = "BackendError";
  }
}

const ROLE_RANK: Record<FileRole, number> = { slide: 0, extra_image: 1, sidecar: 2, original_zip: 3 };

/** Files of a deposit as every screen shows them: slides in order, then extras, descriptive files, the ZIP. */
export function sortFiles<T extends Pick<DepositFileRow, "role" | "position" | "source_path">>(files: T[]): T[] {
  return [...files].sort(
    (a, b) => ROLE_RANK[a.role] - ROLE_RANK[b.role] || (a.position ?? 0) - (b.position ?? 0) || (a.source_path < b.source_path ? -1 : a.source_path > b.source_path ? 1 : 0),
  );
}

/**
 * The error a database function raised (SQLSTATE and message, from PostgreSQL or
 * PostgREST), as the interface shows it. The memory backend raises the same ones.
 */
export function databaseError(sqlstate: string | undefined, message: string): BackendError {
  const detail = message.replace(/^[a-z_]+:\s*/, "");
  switch (sqlstate) {
    case "42501":
      return new BackendError("not_a_member", "Accès réservé aux membres d'OPULANCIA.");
    case "40001":
      return new BackendError("version_conflict", "Ce dépôt vient d'être modifié par ailleurs : recharge-le avant de recommencer.");
    case "P0002":
    case "22P02":
      return new BackendError("not_found", "Dépôt introuvable.");
    case "55000":
      return new BackendError("invalid", "Ce dépôt n'est pas encore sauvegardé (envoi inachevé) : reprends l'envoi d'abord.");
    case "23505":
      if (/instagram/.test(message)) return new BackendError("already_linked", `Ce lien Instagram est déjà associé au dépôt n°${/\d+/.exec(detail)?.[0] ?? "?"}.`);
      return new BackendError("unknown", message);
    case "22023":
      if (message.startsWith("client_key_reused")) return new BackendError("invalid", "Cette tentative d'envoi concernait d'autres fichiers : recommence le dépôt.");
      if (message.startsWith("invalid_link")) {
        if (/future/.test(message)) return new BackendError("invalid", "La date de publication est dans le futur.");
        if (/date needs a link/.test(message)) return new BackendError("invalid", "Une date de publication demande un lien.");
        return new BackendError("invalid", "Lien Instagram invalide (attendu : https://www.instagram.com/p/<code>/).");
      }
      if (message.startsWith("invalid_order")) return new BackendError("invalid", `Ordre refusé : ${detail}.`);
      return new BackendError("invalid", `Dépôt refusé : ${detail}.`);
    default:
      return new BackendError("unknown", message);
  }
}
